/**
 * Vitals monitoring API functions
 */

import { apiClient } from '../api-client';
import type { Vitals } from './types';

export const vitalsApi = {
  /**
   * Get current patient user's vitals
   */
  getMyVitals: async (
    params?: { days?: number; start_date?: string; end_date?: string }
  ): Promise<{ patient_id: number; vitals: Vitals[] }> => {
    return apiClient.get(`/vitals/me`, params);
  },

  /**
   * Record vitals for current patient user
   */
  recordMyVitals: async (data: {
    heart_rate?: number;
    spo2?: number;
    systolic_bp?: number;
    diastolic_bp?: number;
    temperature?: number;
    respiratory_rate?: number;
    weight?: number;
    blood_glucose?: number;
    pain_level?: number;
    notes?: string;
    location?: string;
  }): Promise<{ message: string; vitals_id: number }> => {
    return apiClient.post('/vitals/me', data);
  },

  /**
   * Get patient vitals (with patient ID)
   */
  getPatientVitals: async (
    patientId: number,
    params?: { days?: number; start_date?: string; end_date?: string }
  ): Promise<{ patient_id: number; vitals: Vitals[] }> => {
    return apiClient.get(`/vitals/patient/${patientId}`, params);
  },

  /**
   * Get vitals trends
   */
  getVitalsTrends: async (
    patientId: number,
    params?: { metric?: string; days?: number }
  ): Promise<{
    patient_id: number;
    metric: string;
    period: string;
    trends: Record<string, number[]>;
    dates: string[];
  }> => {
    return apiClient.get(`/vitals/trends/${patientId}`, params);
  },

  /**
   * Record vitals (for healthcare providers)
   */
  recordVitals: async (data: {
    patient_id: number;
    heart_rate?: number;
    spo2?: number;
    systolic_bp?: number;
    diastolic_bp?: number;
    temperature?: number;
    respiratory_rate?: number;
    weight?: number;
    blood_glucose?: number;
    pain_level?: number;
    notes?: string;
    location?: string;
  }): Promise<{ message: string; vitals_id: number }> => {
    return apiClient.post('/vitals', data);
  },

  /**
   * Get latest vitals
   */
  getLatestVitals: async (patientId: number): Promise<Vitals | null> => {
    const response = await apiClient.get<{ patient_id: number; vitals: Vitals[] }>(
      `/vitals/patient/${patientId}`,
      { days: 1 }
    );
    return response.vitals.length > 0 ? response.vitals[0] : null;
  },

  /**
   * ✅ NEW: Get single vitals record by ID
   */
  getVitalsById: async (vitalsId: number): Promise<Vitals> => {
    return apiClient.get(`/vitals/${vitalsId}`);
  },

  /**
   * ✅ NEW: Update vitals record
   */
  updateVitals: async (vitalsId: number, data: {
    heart_rate?: number;
    spo2?: number;
    systolic_bp?: number;
    diastolic_bp?: number;
    temperature?: number;
    respiratory_rate?: number;
    weight?: number;
    notes?: string;
  }): Promise<{ status: string; message: string; vitals_id: number }> => {
    return apiClient.put(`/vitals/${vitalsId}`, data);
  },

  /**
   * ✅ NEW: Delete vitals record
   */
  deleteVitals: async (vitalsId: number): Promise<{ status: string; message: string; vitals_id: number }> => {
    return apiClient.delete(`/vitals/${vitalsId}`);
  },
};

// Standalone function exports for use in hooks
export const getMyVitals = async (
  startDate?: string,
  endDate?: string
): Promise<Vitals[]> => {
  const response = await apiClient.get<{ patient_id: number; vitals: Vitals[] }>(
    `/vitals/me`,
    { start_date: startDate, end_date: endDate }
  );
  return response.vitals;
};

export const createMyVitals = async (data: {
  heart_rate?: number;
  spo2?: number;
  systolic?: number;
  diastolic?: number;
  temperature?: number;
  respiratory_rate?: number;
  weight?: number;
  blood_glucose?: number;
  pain_level?: number;
  notes?: string;
  location?: string;
}): Promise<{ message: string; vitals_id: number }> => {
  // Map field names to match backend
  return apiClient.post('/vitals/me', {
    ...data,
    systolic_bp: data.systolic,
    diastolic_bp: data.diastolic,
  });
};

export const getVitals = async (
  patientId: number,
  startDate?: string,
  endDate?: string
): Promise<Vitals[]> => {
  const response = await apiClient.get<{ patient_id: number; vitals: Vitals[] }>(
    `/vitals/patient/${patientId}`,
    { start_date: startDate, end_date: endDate }
  );
  return response.vitals;
};

export const createVitals = async (data: {
  patient_id: number;
  heart_rate?: number;
  spo2?: number;
  systolic?: number;
  diastolic?: number;
  temperature?: number;
  respiratory_rate?: number;
  weight?: number;
  blood_glucose?: number;
  pain_level?: number;
  notes?: string;
  location?: string;
}): Promise<{ message: string; vitals_id: number }> => {
  // Map field names to match backend
  return apiClient.post('/vitals', {
    ...data,
    systolic_bp: data.systolic,
    diastolic_bp: data.diastolic,
  });
};